import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Plus, FileType } from "lucide-react";
import { listNotes, addTextNote, uploadNote, deleteNote } from "@/lib/api";
import { toast } from "sonner";

export default function NotesTab({ subject }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showText, setShowText] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setNotes(await listNotes(subject.id)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [subject.id]);

  const onFiles = async (files) => {
    for (const f of Array.from(files)) {
      try {
        await uploadNote(subject.id, f);
        toast.success(`Uploaded ${f.name}`);
      } catch (e) {
        toast.error(`Upload failed: ${e?.response?.data?.detail || f.name}`);
      }
    }
    await load();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files);
  };

  const submitText = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addTextNote(subject.id, { title: title || "Untitled note", text });
      setTitle(""); setText(""); setShowText(false);
      toast.success("Note added");
      load();
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await deleteNote(id);
    setNotes((n) => n.filter((x) => x.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Upload zone */}
      <div
        data-testid="upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        onClick={() => fileInput.current?.click()}
        className={`nb-border rounded-2xl p-10 text-center cursor-pointer transition-all ${
          drag ? "bg-[#FFE066] nb-shadow-lg" : "bg-white dark:bg-zinc-900 nb-shadow"
        }`}
      >
        <Upload className="w-10 h-10 mx-auto mb-3" strokeWidth={2.5} />
        <div className="font-display text-2xl font-extrabold mb-1">Drop notes here</div>
        <div className="text-zinc-600 dark:text-zinc-400 font-medium text-sm">
          Drag-and-drop PDF, DOCX, TXT, or MD files, or click to choose
        </div>
        <input
          ref={fileInput}
          data-testid="file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md"
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      <div className="flex justify-end">
        <button
          data-testid="add-text-note-btn"
          onClick={() => setShowText((v) => !v)}
          className="nb-button px-4 py-2 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Text Note
        </button>
      </div>

      {showText && (
        <form onSubmit={submitText} className="nb-card p-6 space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            data-testid="text-note-title"
            className="nb-input"
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder="Paste or type your notes here…"
            data-testid="text-note-content"
            className="nb-input resize-y"
          />
          <button
            type="submit"
            disabled={saving || !text.trim()}
            data-testid="text-note-save"
            className="nb-button px-5 py-2 bg-[#D4C4FB] dark:bg-[#B794F4]"
          >
            {saving ? "Saving…" : "Save Note"}
          </button>
        </form>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="font-bold text-zinc-500">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-zinc-500 font-medium">No notes yet, drop a file or add text above.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-${n.id}`} className="nb-card p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg nb-border bg-[#90E0FF] dark:bg-[#63B3ED] flex items-center justify-center">
                  {n.kind === "upload" ? <FileType className="w-4 h-4 text-black" /> : <FileText className="w-4 h-4 text-black" />}
                </div>
                <div className="font-bold truncate flex-1">{n.title}</div>
                <button onClick={() => remove(n.id)} data-testid={`delete-note-${n.id}`} className="nb-border rounded-lg p-1.5 hover:bg-red-100 dark:hover:bg-red-900">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs font-bold text-zinc-500">
                {n.kind === "upload" ? "Uploaded file" : "Text note"} · {(n.size || 0) > 1024 ? `${Math.round(n.size/1024)} KB` : `${n.size} chars`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
